/**
 * Story SF-CHAT-9.6 — entrada pelo app-v2 (APP-V2-5.2 do app principal).
 *
 * O app abre o chat noutra aba com `?entrada=app` (nada secreto na URL). A
 * sessão passa por `postMessage` entre as duas abas:
 *
 *   chat → app  {tipo: "pronto"}                 o chat carregou
 *   app  → chat {tipo: "ola", usuario, empresa}  quem é e qual empresa (só para a tela)
 *   chat → app  {tipo: "pedir-grant", pedido}    um grant para a sessão ou para um turno
 *   app  → chat {tipo: "grant", pedido, grant}   grant de uso único, 5 min, da empresa escolhida no app
 *   app  → chat {tipo: "erro", pedido, motivo}   reauth | forbidden | unavailable
 *   app  → chat {tipo: "encerrar"}               logout no app ou outra aba do assistente
 *
 * O app é o corretor: o token dele nunca sai da aba do app. O chat só aceita
 * mensagem da janela que o abriu (`event.source === opener`) e de uma origem da
 * lista (`VITE_APP_ORIGINS`, ou a origem de `VITE_APP_API_BASE_URL`); depois do
 * olá, só daquela origem. Responde sempre com `targetOrigin` explícito, nunca "*".
 *
 * Implementa o mesmo contrato de `createAppSession` que a tela live usa, para o
 * `LiveChat` não saber de onde vêm os grants.
 */
import { AppSessionError, type AppUser, type StoredSession } from "./appSession.ts";

export const CANAL = "sf-chat-handoff";
const VERSAO = 1;

interface MessageEventLike {
  data: unknown;
  origin: string;
  source: unknown;
}

interface WindowLike {
  addEventListener(type: "message", fn: (e: MessageEventLike) => void): void;
  removeEventListener(type: "message", fn: (e: MessageEventLike) => void): void;
}

interface OpenerLike {
  closed?: boolean;
  postMessage(data: unknown, targetOrigin: string): void;
}

const FECHADO =
  "A aba do aplicativo que abriu este assistente foi fechada ou saiu da conta. Volte ao aplicativo e abra o Assistente de novo.";
const SEM_RESPOSTA = "O aplicativo não respondeu ao pedido de autorização. Tente de novo em instantes.";
const SEM_OLA =
  "Não recebemos a autorização do aplicativo. Feche esta aba e abra o Assistente de novo pelo aplicativo.";
const PROIBIDO = "O aplicativo não autorizou o chat para esta empresa. Confira seu acesso no aplicativo.";
const SEM_APP =
  "A aba do aplicativo deixou de responder (foi recarregada ou saiu da conta). Volte ao aplicativo e abra o Assistente de novo.";
/** Timeouts seguidos com a aba do app viva (recarregada: não sabe mais desta abertura). */
const MAX_TIMEOUTS = 2;
const SO_PELO_APP = "Este assistente foi aberto pelo aplicativo. Para entrar de novo, abra o Assistente pelo aplicativo.";

const RE_HANDOFF = /^[A-Za-z0-9_-]{8,64}$/;

/** Id (não secreto) da abertura feita pelo app, no `#h=…`. Inválido ou ausente: null. */
export function handoffIdFrom(hash: string): string | null {
  try {
    const id = new URLSearchParams(String(hash || "").replace(/^#/, "")).get("h");
    return id && RE_HANDOFF.test(id) ? id : null;
  } catch {
    return null;
  }
}

/** `?entrada=app`, `#h=<id>` e uma aba que abriu esta: só então o chat espera o app. */
export function wantsHandoff(search: string, opener: unknown, hash: string): boolean {
  if (!opener || !handoffIdFrom(hash)) return false;
  try {
    return new URLSearchParams(search).get("entrada") === "app";
  } catch {
    return false;
  }
}

function origemValida(valor: string): string | null {
  try {
    const u = new URL(valor.trim());
    return u.protocol === "http:" || u.protocol === "https:" ? u.origin : null;
  } catch {
    return null;
  }
}

/** Origens de app aceitas. Nunca "*": valor que não é URL http(s) é descartado. */
export function handoffOrigins(env: { VITE_APP_ORIGINS?: string; VITE_APP_API_BASE_URL?: string }): string[] {
  const lista = env.VITE_APP_ORIGINS
    ? env.VITE_APP_ORIGINS.split(",")
    : env.VITE_APP_API_BASE_URL
      ? [env.VITE_APP_API_BASE_URL]
      : [];
  return [...new Set(lista.map(origemValida).filter((o): o is string => Boolean(o)))];
}

const ehTexto = (v: unknown, max = 512): v is string => typeof v === "string" && v.length > 0 && v.length <= max;

function paraUsuario(d: Record<string, unknown>): AppUser | null {
  const u = d.usuario as Record<string, unknown> | undefined;
  const e = d.empresa as Record<string, unknown> | undefined;
  if (!u || typeof u.id !== "number" || !ehTexto(u.nome, 200)) return null;
  if (!e || typeof e.id !== "number" || typeof e.nome !== "string") return null;
  return { id: u.id, nome: u.nome, empresaId: e.id, empresaNome: e.nome || null };
}

export function createHandoffSession(options: {
  win: WindowLike;
  opener: OpenerLike;
  allowedOrigins: string[];
  /** Id da abertura (`#h=`), devolvido em cada mensagem; o app só aceita o corrente. */
  handoffId: string;
  timeoutMs?: number;
  helloTimeoutMs?: number;
  newId?: () => string;
}) {
  const { win, opener, allowedOrigins, handoffId } = options;
  const timeoutMs = options.timeoutMs ?? 15000;
  const helloTimeoutMs = options.helloTimeoutMs ?? 10000;
  const newId =
    options.newId ?? (() => (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`));

  let origem: string | null = null;
  let user: AppUser | null = null;
  let encerrada = false;
  let timeoutsSeguidos = 0;
  let aoOla: ((u: AppUser) => void) | null = null;
  const pendentes = new Map<string, { ok: (g: string) => void; falha: (e: AppSessionError) => void; relogio: ReturnType<typeof setTimeout> }>();

  const enviar = (dados: Record<string, unknown>, destino: string) =>
    opener.postMessage({ canal: CANAL, v: VERSAO, handoff: handoffId, ...dados }, destino);

  function encerrar(motivo = FECHADO) {
    encerrada = true;
    user = null;
    for (const [id, p] of pendentes) {
      clearTimeout(p.relogio);
      pendentes.delete(id);
      p.falha(new AppSessionError(motivo, "reauth"));
    }
  }

  function ouvir(e: MessageEventLike) {
    if (e.source !== opener) return;
    if (origem ? e.origin !== origem : !allowedOrigins.includes(e.origin)) return;
    const d = e.data as Record<string, unknown> | null;
    if (!d || typeof d !== "object" || d.canal !== CANAL || d.v !== VERSAO) return;
    if (d.handoff !== handoffId) return; // outra abertura (outra aba do app, aba antiga)

    if (d.tipo === "ola" && !origem && !encerrada) {
      const u = paraUsuario(d);
      if (!u) return;
      origem = e.origin;
      user = u;
      aoOla?.(u);
      return;
    }
    if (!origem) return;
    if (d.tipo === "encerrar") {
      encerrar();
      return;
    }
    if ((d.tipo === "grant" || d.tipo === "erro") && ehTexto(d.pedido, 64)) {
      const p = pendentes.get(d.pedido);
      if (!p) return;
      if (d.tipo === "grant") {
        if (!ehTexto(d.grant, 512)) return;
        clearTimeout(p.relogio);
        pendentes.delete(d.pedido);
        timeoutsSeguidos = 0;
        p.ok(d.grant);
        return;
      }
      clearTimeout(p.relogio);
      pendentes.delete(d.pedido);
      if (d.motivo === "reauth") {
        encerrar();
        p.falha(new AppSessionError(FECHADO, "reauth"));
      } else if (d.motivo === "forbidden") {
        p.falha(new AppSessionError(PROIBIDO, "forbidden"));
      } else {
        p.falha(new AppSessionError(SEM_RESPOSTA, "unavailable"));
      }
    }
  }
  win.addEventListener("message", ouvir);

  /** Avisa o app e espera o olá. Resolve com quem está entrando. Idempotente (StrictMode). */
  let conexao: Promise<AppUser> | null = null;
  function connect(): Promise<AppUser> {
    conexao ??= new Promise((resolve, reject) => {
      const relogio = setTimeout(() => {
        aoOla = null;
        reject(new AppSessionError(SEM_OLA, "unavailable"));
      }, helloTimeoutMs);
      aoOla = (u) => {
        clearTimeout(relogio);
        aoOla = null;
        resolve(u);
      };
      for (const destino of allowedOrigins) enviar({ tipo: "pronto" }, destino);
    });
    return conexao;
  }

  function issueGrant(): Promise<string> {
    if (encerrada || !origem || !user) return Promise.reject(new AppSessionError(FECHADO, "reauth"));
    if (opener.closed) {
      encerrar();
      return Promise.reject(new AppSessionError(FECHADO, "reauth"));
    }
    const pedido = newId();
    const destino = origem;
    return new Promise((ok, falha) => {
      const relogio = setTimeout(() => {
        pendentes.delete(pedido);
        if (opener.closed) {
          encerrar();
          falha(new AppSessionError(FECHADO, "reauth"));
          return;
        }
        timeoutsSeguidos += 1;
        if (timeoutsSeguidos >= MAX_TIMEOUTS) {
          // a aba viva não responde mais (recarregada): sem beco sem saída, volta ao app
          encerrar(SEM_APP);
          falha(new AppSessionError(SEM_APP, "reauth"));
          return;
        }
        falha(new AppSessionError(SEM_RESPOSTA, "unavailable"));
      }, timeoutMs);
      pendentes.set(pedido, { ok, falha, relogio });
      enviar({ tipo: "pedir-grant", pedido }, destino);
    });
  }

  function current(): StoredSession | null {
    return user ? { access: "", user } : null;
  }

  async function listCompanies() {
    if (!user || user.empresaId === null) throw new AppSessionError(FECHADO, "reauth");
    return [{ id: user.empresaId, nome: user.empresaNome ?? "" }];
  }

  function chooseCompany(): AppUser {
    if (!user) throw new AppSessionError(FECHADO, "reauth");
    return user;
  }

  const soPeloApp = async (): Promise<never> => {
    throw new AppSessionError(SO_PELO_APP, "reauth");
  };

  return {
    connect,
    issueGrant,
    current,
    listCompanies,
    chooseCompany,
    signIn: soPeloApp as (email: string, password: string) => Promise<never>,
    verifySecondFactor: soPeloApp as (challenge: string, code: string) => Promise<never>,
    signOut: () => encerrar(SO_PELO_APP),
    dispose: () => win.removeEventListener("message", ouvir),
  };
}

export type HandoffSession = ReturnType<typeof createHandoffSession>;
